import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  catchError,
  of,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '../../environments/environment';
import {
  API_CALENDAR,
  API_COMMON,
  API_DISCOUNT,
  API_EVENT,
  API_EVENT_PLAYER,
  API_FEATURE,
  API_MEDIA,
  API_NSID_VERIFICATION,
  API_PAYMENT_BOOKING,
  API_PAYMENT_PLAN,
  API_PRODUCT,
  API_TOURNAMENT,
} from '../helpers/api_constant';
import { IWebSettingsPages, TypeFormat } from '../types/model.types';
import { SafeWindow } from '../utils/safe-window';
import { showAlertMessage, showImportErrorsAlertV2 } from '../utils/utils';
import { WebsiteGateService } from './website-gate.service';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  win = SafeWindow.get();

  // Cache configuration: { key: { data, timestamp } }
  private responseCache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_DURATIONS: { [key: string]: number } = {
    getClientData: 10 * 60 * 1000, // 10 minutes
    getTournamentById: 15 * 60 * 1000, // 15 minutes
    getEventById: 5 * 60 * 1000, // 5 minutes
    getCustomFormListById: 30 * 60 * 1000, // 30 minutes
  };

  constructor(
    private http: HttpClient,
    public router: Router,
    private websiteGateService: WebsiteGateService,
  ) {}

  private paymentPlanDateLabelStore = new BehaviorSubject<string>('');
  paymentPlanDateLabel$ = this.paymentPlanDateLabelStore.asObservable();
  private webSettingPagesStore = new BehaviorSubject<IWebSettingsPages | null>(
    null,
  );
  webSettingPagesStore$ = this.webSettingPagesStore.asObservable();

  /**
   * Get cached response if available and not expired
   * @param cacheKey Unique cache key
   * @returns Cached data or null if expired/not found
   */
  private getFromCache(cacheKey: string): any {
    const cached = this.responseCache.get(cacheKey);
    if (cached) {
      const duration =
        this.CACHE_DURATIONS[cacheKey.split('_')[0]] || 5 * 60 * 1000;
      if (Date.now() - cached.timestamp < duration) {
        return cached.data;
      } else {
        this.responseCache.delete(cacheKey);
      }
    }
    return null;
  }

  /**
   * Store response in cache
   * @param cacheKey Unique cache key
   * @param data Response data to cache
   */
  private setInCache(cacheKey: string, data: any): void {
    this.responseCache.set(cacheKey, { data, timestamp: Date.now() });
  }

  /**
   * Clear specific cache entry or all cache
   * @param cacheKey Optional specific key to clear; if not provided, clears all
   */
  clearCache(cacheKey?: string): void {
    if (cacheKey) {
      this.responseCache.delete(cacheKey);
    } else {
      this.responseCache.clear();
    }
  }

  updatePaymentPlanDateLabel(dateLabel: string) {
    this.paymentPlanDateLabelStore.next(dateLabel);
  }

  setWebSettingPagesStore(value: IWebSettingsPages) {
    this.webSettingPagesStore.next(value);
  }

  private handleError(error: any) {
    let errorMessage = 'Something bad happened; please try again later.';
    if (error?.error instanceof ErrorEvent) {
      errorMessage = error.error.message || 'Something went wrong!';
    } else if (error?.error?.message) {
      errorMessage = error.error.message;
    } else if (typeof error?.error === 'string') {
      errorMessage = error.error;
    } else if (error?.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    const errorObj = error?.error || error;
    if (errorObj?.errors?.length) {
      showImportErrorsAlertV2(
        errorObj?.message || 'Import failed',
        errorObj.errors,
      );
    } else {
      showAlertMessage(errorMessage, 'error');
    }

    // Return an observable with the actual user-facing error message.
    return throwError(() => errorMessage);
  }

  getClientData(clientName: any): Observable<any> {
    if (!clientName) {
      return of(null);
    }

    const cacheKey = `getClientData_${clientName}`;
    const cachedData = this.getFromCache(cacheKey);
    if (cachedData) {
      return of(cachedData);
    }

    return this.http
      .get(environment.API + API_COMMON.WEB_SETTING + '?name=' + clientName)
      .pipe(
        tap((data: any) => this.setInCache(cacheKey, data)),
        catchError((error: any) => {
          // Handle the error here
          if (error.error) {
            if (this.win) {
              window.location.href = 'https://sketchplay.com';
            }
          }
          // Optionally, throw the error to propagate it to the caller
          throw error;
        }),
      );
  }

  postContactUs(payload: any): Observable<any> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    let options = { headers: headers };
    return this.http.post<any>(
      `${environment.API}${API_COMMON.CONTACT_US}`,
      payload,
      options,
    );
  }

  logIn(payload: any): Observable<any> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    let options = { headers: headers };
    return this.http
      .post<any>(`${environment.API}${API_COMMON.WEB_LOGIN}`, payload, options)
      .pipe(catchError(this.handleError));
  }

  getEventList(
    orgId: number,
    type: string,
    offset: number,
    lmit: number,
  ): Observable<any> {
    const currentDateTime = this.getCurrentDateTime();
    return this.http.get(
      `${environment.API}${API_EVENT.MARKETING}?skip=${offset}&limit=${lmit}&orgId=${orgId}&type=${type}&startDate=${currentDateTime?.startDate}&date=${currentDateTime?.startDate}&time=${currentDateTime?.currentTime}&timeZone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
    );
  }
  getTournamentList(
    orgId: number,
    offset: number,
    lmit: number,
    pastTournaments?: boolean,
  ): Observable<any> {
    let url = `${environment.API}${API_TOURNAMENT.MARKETING}?skip=${offset}&limit=${lmit}&orgId=${orgId}`;
    if (pastTournaments) {
      url += `&isPast=true`;
    }
    return this.http.get(url);
  }
  getTournamentById(
    id: number,
    bypassCache = false,
    isFromSelfRegistration = false,
    skipGlobalError = false,
  ): Observable<any> {
    const cacheKey = `getTournamentById_${id}`;
    const cachedData = bypassCache ? null : this.getFromCache(cacheKey);
    if (cachedData) {
      this.websiteGateService.setRegistrationOrg(
        cachedData?.data?.organization?.id,
      );
      return of(cachedData);
    }

    return this.http
      .get(
        `${environment.API}${API_TOURNAMENT.MARKETING}/${id}?isFromSelfRegistration=${isFromSelfRegistration}`,
      )
      .pipe(
        tap((data: any) => {
          this.setInCache(cacheKey, data);
          this.websiteGateService.setRegistrationOrg(
            data?.data?.organization?.id,
          );
        }),
        catchError((error: any) => {
          // The backend returns a non-2xx status for "Tournament does not
          // exist" along with an { isHide } flag the marketing registration
          // pages need to decide where to redirect — skipGlobalError lets
          // those callers handle the alert themselves instead of the
          // generic one-size-fits-all handleError below.
          if (skipGlobalError) {
            return throwError(() => error);
          }
          return this.handleError(error);
        }),
      );
  }
  getTournamentProduct(id: number): Observable<any> {
    return this.http.get(
      `${environment.API}${API_TOURNAMENT.MARKETING_PRODUCTS}/${id}`,
    );
  }
  getPastEventList(
    orgId: number,
    type: string,
    offset: number,
    lmit: number,
  ): Observable<any> {
    const currentDateTime = this.getCurrentDateTime();
    return this.http.get(
      `${environment.API}${API_EVENT.MARKETING}?skip=${offset}&limit=${lmit}&orgId=${orgId}&type=${type}&pastEvents=true&date=${currentDateTime?.startDate}&time=${currentDateTime?.currentTime}&timeZone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
    );
  }

  getDailyTrainingEventList(orgId: number): Observable<any> {
    const currentDateTime = this.getCurrentDateTime();
    return this.http.get(
      `${environment.API}${API_EVENT.DAILY_TRAINING}?orgId=${orgId}&startDate=${currentDateTime?.startDate}&date=${currentDateTime?.startDate}&time=${currentDateTime?.currentTime}&timeZone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
    );
  }

  getEventById(eventId: number, orgId: number): Observable<any> {
    const cacheKey = `getEventById_${eventId}_${orgId}`;
    const cachedData = this.getFromCache(cacheKey);
    if (cachedData) {
      this.websiteGateService.setRegistrationOrg(
        cachedData?.data?.organization?.id,
      );
      return of(cachedData);
    }

    const currentDateTime = this.getCurrentDateTime();
    return this.http
      .get(
        `${environment.API}${API_EVENT.MARKETING}/${eventId}?date=${currentDateTime?.startDate}&time=${currentDateTime?.currentTime}&timeZone=${Intl.DateTimeFormat().resolvedOptions().timeZone}&orgId=${orgId}`,
      )
      .pipe(
        tap((data: any) => {
          this.setInCache(cacheKey, data);
          this.websiteGateService.setRegistrationOrg(
            data?.data?.organization?.id,
          );
        }),
      );
  }
  uploadDocumentName(
    payload: any,
    typeFormat: TypeFormat = TypeFormat.MEDIA,
    isTournamentRosterFile?: boolean,
    isForRosterDocument?: boolean,
    registrationId?: any,
    tournamentId?: any,
    skipGlobalError?: boolean,
  ): Observable<any> {
    const formdata = new FormData();
    formdata.append('file', payload, payload?.name);

    let url = `${environment.API}${API_MEDIA.DOCUMENT_NO_AUTH_NAME}?isTournamentRosterFile=${isTournamentRosterFile ?? false}&isForRosterDocument=${isForRosterDocument ?? false}`;
    if (registrationId) {
      url += `&registrationId=${registrationId}`;
    }
    if (tournamentId) {
      url += `&tournamentId=${tournamentId}`;
    }
    if (typeFormat) {
      url += `&type=${typeFormat}`;
    }

    const headers = new HttpHeaders({ 'x-api-key': environment.mediaApiKey });
    return this.http.post<any>(url, formdata, { headers }).pipe(
      catchError((error: any) => {
        if (skipGlobalError) {
          return throwError(() => error);
        }
        return this.handleError(error);
      }),
    );
  }
  downloadDocument(payload: { documents: string[] | string }): Observable<any> {
    return this.http
      .post<any>(
        `${environment.API}${API_MEDIA.DOWNLOAD_DOCUMENT_NO_AUTH}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
  registerEventPlayer(payload: any): Observable<any> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    let options = { headers: headers };
    return this.http.post<any>(
      `${environment.API}${API_EVENT_PLAYER.USER_CHECK}`,
      payload,
      options,
    );
  }

  checkEventAthlete(payload: any): Observable<any> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    let options = { headers: headers };
    return this.http.post<any>(
      `${environment.API}${API_EVENT_PLAYER.CHECK_REGISTER_ATHLETE}`,
      payload,
      options,
    );
  }

  getEventInsurance(reqData: any) {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    let options = { headers: headers };
    return this.http
      .post<any>(`${environment.API}${API_EVENT.INSURANCE}`, reqData, options)
      .pipe(catchError(this.handleError));
  }

  eventPaymentIntent(payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const options = { headers: headers };
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_EVENT_PLAYER.EVENT_MARKETING_INTENT}`,
        payload,
        options,
      )
      .pipe(catchError(this.handleError));
  }

  eventRegister(payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const options = { headers: headers };
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_EVENT_PLAYER.EVENT_REGISTER}`,
        payload,
        options,
      )
      .pipe(catchError(this.handleError));
  }

  // Saves the booking body BEFORE Stripe confirms the payment so the server can book it
  // later if this page never posts it (3DS bank-app switch, redirect, closed tab).
  savePaymentBooking(body: {
    paymentIntentId: string;
    stripeAccountId: string;
    flow: string;
    payload: any;
  }): Observable<any> {
    return this.http
      .post<any>(`${environment.API}${API_PAYMENT_BOOKING.SAVE_PUBLIC}`, {
        ...body,
        payload: {
          ...body.payload,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      })
      .pipe(catchError(this.handleError));
  }

  eventFreeRegister(payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const options = { headers: headers };
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_EVENT_PLAYER.EVENT_REGISTER_FREE}`,
        payload,
        options,
      )
      .pipe(catchError(this.handleError));
  }

  getCurrentDateTime() {
    const currentDate = new Date();

    // Get current date components
    const day = currentDate.getDate().toString().padStart(2, '0');
    const month = (currentDate.getMonth() + 1).toString().padStart(2, '0'); // Month is zero-based, so we add 1
    const year = currentDate.getFullYear();

    // Get current time components
    let hours = currentDate.getHours();
    const minutes = currentDate.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    const h = hours ? hours : 12; // the hour '0' should be '12'
    const hoursStr = h.toString().padStart(2, '0');

    // Combine date and time
    const startDate = `${year}-${month}-${day}`;
    const currentTime = `${hoursStr}:${minutes} ${ampm}`;
    const obj = {
      startDate: startDate,
      currentTime: currentTime,
    };
    return obj;
  }

  getGrades(): Observable<any> {
    return this.http
      .get(`${environment.API}${API_FEATURE.GET_GRADES}`)
      .pipe(catchError(this.handleError));
  }

  getAllStates(): Observable<any> {
    return this.http.get(environment.API + API_FEATURE.GET_ALL_STATES);
  }

  getAllCities(stateCode: string): Observable<any> {
    return this.http.get(
      environment.API + `${API_FEATURE.GET_ALL_CITIES}/${stateCode}`,
    );
  }
  checkDiscount(payload: any): Observable<any> {
    return this.http
      .post<any>(`${environment.API}${API_DISCOUNT.CHECK_NO_AUTH}`, payload)
      .pipe(catchError(this.handleError));
  }
  checkLacroseNumber(usaLacrosseNumber: any): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API}${API_EVENT.LACROSSE_CHECK}?lacrosseId=${usaLacrosseNumber}`,
      )
      .pipe(catchError(this.handleError));
  }

  getProductList(payload: any): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API}${API_PRODUCT.MARKETING_LIST}?orgId=${payload?.orgId}&eventId=${payload?.eventId}`,
      )
      .pipe(catchError(this.handleError));
  }
  getSubscriptionList(payload: any): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API}${API_PRODUCT.MARKETING_SUBSCRIPTION_LIST}?orgId=${payload?.orgId}&eventId=${payload?.eventId}`,
      )
      .pipe(catchError(this.handleError));
  }
  checkInfo(payload: any): Observable<any> {
    return this.http
      .post<any>(`${environment.API}${API_COMMON.USER_BY_INFO}`, payload)
      .pipe(catchError(this.handleError));
  }
  productPaymentIntent(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(`${environment.API}${API_PRODUCT.PRODUCT_INTENT}`, payload)
      .pipe(catchError(this.handleError));
  }
  productSubscriptionPaymentIntent(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_PRODUCT.PRODUCT_SUBSCRIPTION_INTENT}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
  purchaseProduct(payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const options = { headers: headers };
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_PRODUCT.PRODUCT_PAYMENT}`,
        payload,
        options,
      )
      .pipe(catchError(this.handleError));
  }
  purchaseSubscriptionProduct(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_PRODUCT.PRODUCT_SUBSCRIPTION_PAYMENT}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
  checkDiscountForProduct(payload: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.API}${API_DISCOUNT.MARKETING_PACKAGE_CHECK}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
  getEligibleProduct(payload: any): Observable<any> {
    return this.http
      .post<any>(`${environment.API}${API_PRODUCT.ELIGIBLE_PRODUCT}`, payload)
      .pipe(catchError(this.handleError));
  }
  checkProduct(id: any, payload: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.API}${API_PRODUCT.CHECK_PRODUCT}/${id}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
  productPaymentRegister(payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const options = { headers: headers };
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_EVENT_PLAYER.EVENT_PAYMENT_PRODUCT}`,
        payload,
        options,
      )
      .pipe(catchError(this.handleError));
  }
  checkSKBalacne(payload: any): Observable<any> {
    return this.http
      .post<any>(`${environment.API}${API_COMMON.SKETCHPLAY_BUCKS}`, payload)
      .pipe(catchError(this.handleError));
  }
  getFeatureList(orgId: any): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API}${API_FEATURE.GET_ACTIVE_FEATURE}?orgId=${orgId}`,
      )
      .pipe(catchError(this.handleError));
  }
  getPaymentPlansDates(payload: { date: string }): Observable<any> {
    return this.http
      .post<any>(
        `${environment.API}${API_PAYMENT_PLAN.MARKETING_DATE}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
  checkPaymentPlanCalculation(payload: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.API}${API_PAYMENT_PLAN.MARKETING_EVENT_CALCULATION}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
  eventPlansPaymentIntent(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_EVENT_PLAYER.EVENT_INTENT_PAYMENT_PLAN}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
  eventPlansCompletePayment(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_EVENT_PLAYER.EVENT_PAYMENT_PAYMENT_PLAN}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
  getProductDetail(payload: any): Observable<any> {
    return this.http.get<any>(
      `${environment.API}${API_PRODUCT.MARKETING_LIST}/${payload?.productId}?orgId=${payload?.orgId}`,
    );
  }
  freeProductBuy(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_PRODUCT.PRODUCT_PAYMENT_FREE}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
  checkProductPaymentPlanCalculation(payload: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.API}${API_PAYMENT_PLAN.MARKETING_PRODUCT_CALCULATION}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
  productPaymentPlanIntent(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_PRODUCT.PRODUCT_INTENT_PAYMENT_PLAN}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
  productPaymentPlanPayment(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_PRODUCT.PRODUCT_PAYMENT_PAYMENT_PLAN}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
  getProductDetails(id: number): Observable<any> {
    return this.http
      .get<any>(`${environment.API}${API_PRODUCT.MARKETING_DETAIL}/${id}`)
      .pipe(catchError(this.handleError));
  }
  getCalenderList(payload: {
    orgId: string;
    classType: string;
    startDate: string;
    endDate?: string;
  }): Observable<any> {
    const params = new URLSearchParams({
      orgId: payload.orgId || '',
      classTypes: payload.classType || '',
      startDate: payload.startDate || '',
    });

    // Add `endDate` only if it exists
    if (payload.endDate) {
      params.append('endDate', payload.endDate);
    }

    return this.http
      .get<any>(
        `${environment.API}${API_CALENDAR.GET_CALENDAR}?${params}&date=${Date.now()}`,
      )
      .pipe(catchError(this.handleError));
  }

  getTournamentCalenderList(
    orgId: string,
    payload: { startDate: string; endDate?: string; tournamentIds: any },
  ): Observable<any> {
    const params = new URLSearchParams({
      startDate: payload.startDate || '',
    });

    // Add `endDate` only if it exists
    if (payload.endDate) {
      params.append('endDate', payload.endDate);
    }
    if (payload?.tournamentIds) {
      params.append('tournamentIds', payload?.tournamentIds);
    }

    return this.http
      .get<any>(
        `${environment.API}${API_CALENDAR.GET_TOURNAMENT_CALENDAR}/${orgId}?${params}&date=${Date.now()}`,
      )
      .pipe(catchError(this.handleError));
  }
  getGameCalenderList(
    orgId: string,
    payload: { startDate: string; endDate?: string; tournamentIds: any },
  ): Observable<any> {
    const params = new URLSearchParams({
      startDate: payload.startDate || '',
    });

    if (payload.endDate) {
      params.append('endDate', payload.endDate);
    }
    if (payload?.tournamentIds) {
      params.append('tournamentIds', payload?.tournamentIds);
    }

    return this.http
      .get<any>(
        `${environment.API}${API_CALENDAR.GET_GAME_CALENDAR}/${orgId}?${params}&date=${Date.now()}`,
      )
      .pipe(catchError(this.handleError));
  }

  getTournamentListFilter(orgId: any): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API}${API_CALENDAR.GET_TOURNAMENT_CALENDAR}/${orgId}?orgId=${orgId}&date=${Date.now()}`,
      )
      .pipe(catchError(this.handleError));
  }

  tournamentRegistrationIntentStripeCard(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(`${environment.API}${API_TOURNAMENT.PAYMENT_INTENT}`, payload)
      .pipe(catchError(this.handleError));
  }

  // Returns { isAllowSketchplayBucks, isUserExist, totalSKBucks } for the primary contact typed on
  // the registration form. The Sketchplay Bucks control is only shown when both flags are true.
  tournamentCheckSketchplayBucks(payload: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.API}${API_TOURNAMENT.SKETCHPLAY_BUCKS_CHECK}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  tournamentCompletetRegistration(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(`${environment.API}${API_TOURNAMENT.REGISTRATION}`, payload)
      .pipe(catchError(this.handleError));
  }

  tournamentSelfRegistration(payload: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.API}${API_TOURNAMENT.SELF_REGISTRATION}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  // Response: an array of { id, name } for the given tournament.
  getSelfRegistrationTeams(tournamentId: number): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API}${API_TOURNAMENT.SELF_REGISTRATION_TEAMS}/${tournamentId}`,
      )
      .pipe(catchError(this.handleError));
  }

  tournamentCheckDiscount(
    payload: any,
    skipGlobalError?: boolean,
  ): Observable<any> {
    return this.http
      .post<any>(`${environment.API}${API_TOURNAMENT.APPLY_DISCOUNT}`, payload)
      .pipe(
        catchError((error: any) => {
          if (skipGlobalError) {
            return throwError(() => error);
          }
          return this.handleError(error);
        }),
      );
  }

  tournamentRegistrationIntentACH(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_TOURNAMENT.ACH_SETUP_INTENT}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  tournamentRegistrationCreateIntentACH(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_TOURNAMENT.ACH_PAYMENT_INTENT}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  // Tournament payment-plan endpoints — mirror the event equivalents
  // (checkPaymentPlanCalculation / eventPlansPaymentIntent / eventPlansCompletePayment).
  checkTournamentPaymentPlanCalculation(payload: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.API}${API_PAYMENT_PLAN.MARKETING_TOURNAMENT_CALCULATION}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  tournamentPlansPaymentIntent(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_TOURNAMENT.INTENT_PAYMENT_PLAN}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  tournamentPlansCompletePayment(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_TOURNAMENT.PAYMENT_PAYMENT_PLAN}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  checkTournamentDownpaymentCalculation(payload: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.API}${API_TOURNAMENT.DOWNPAYMENT_CALCULATION}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  tournamentDownpaymentIntent(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_TOURNAMENT.INTENT_DOWNPAYMENT}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  tournamentDownpaymentCompletePayment(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_TOURNAMENT.PAYMENT_DOWNPAYMENT}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  getRegisteredTournamentById(registeredTournamentId: number): Observable<any> {
    return this.http
      .get(
        `${environment.API}${API_TOURNAMENT.REGISTER_TEAMS}/${registeredTournamentId}`,
      )
      .pipe(catchError(this.handleError));
  }
  getDocuments(id: number): Observable<any> {
    return this.http
      .get(
        `${environment.API}${API_TOURNAMENT.REGISTER_TEAM_ROSTER_DOCUMENTS}/${id}`,
      )
      .pipe(catchError(this.handleError));
  }
  getTeamInfoById(registeredTournamentId: number): Observable<any> {
    return this.http
      .get(
        `${environment.API}${API_TOURNAMENT.REGISTER_TEAM}/${registeredTournamentId}`,
      )
      .pipe(catchError(this.handleError));
  }

  tournamentUpdateRegisterTeamRoster(payload: any): Observable<any> {
    return this.http
      .patch<any>(
        `${environment.API}${API_TOURNAMENT.UPDATE_REGISTER_TEAM_ROSTER}/${payload?.registeredTournamentId}`,
        { ...payload, registeredTournamentId: undefined },
      )
      .pipe(catchError(this.handleError));
  }

  addManualRosterPlayer(payload: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.API}${API_TOURNAMENT.ADD_MANUAL_ROSTER_PLAYER}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  // BUG-009: one roster player (spreadsheet or manually added), via the manager's registration link only.
  getRosterPlayer(
    teamId: number,
    type: string,
    id: string | number,
    registrationId: string,
  ): Observable<any> {
    const params = new HttpParams().set('registrationId', registrationId);
    return this.http
      .get<any>(
        `${environment.API}${API_TOURNAMENT.ROSTER_PLAYER}/${teamId}/${type}/${id}`,
        { params },
      )
      .pipe(catchError(this.handleError));
  }

  updateRosterPlayer(payload: any): Observable<any> {
    return this.http
      .patch<any>(`${environment.API}${API_TOURNAMENT.ROSTER_PLAYER}`, payload)
      .pipe(catchError(this.handleError));
  }

  deleteRosterPlayer(
    teamId: number,
    type: string,
    id: string | number,
    registrationId: string,
  ): Observable<any> {
    const params = new HttpParams().set('registrationId', registrationId);
    return this.http
      .delete<any>(
        `${environment.API}${API_TOURNAMENT.ROSTER_PLAYER}/${teamId}/${type}/${id}`,
        { params },
      )
      .pipe(catchError(this.handleError));
  }

  checkTournamentCostSpots(payload: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.API}${API_TOURNAMENT.CHECK_COST_WISE_SPOT}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  validateLacrosseNumber(lacrosseNumber: number): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API}${API_EVENT.LACROSSE_CHECK}?lacrosseId=${lacrosseNumber}`,
      )
      .pipe(catchError(this.handleError));
  }
  getCustomFormListById(id: any): Observable<any> {
    const cacheKey = `getCustomFormListById_${id}`;
    const cachedData = this.getFromCache(cacheKey);
    if (cachedData) {
      return of(cachedData);
    }

    return this.http
      .get<any>(`${environment.API}${API_COMMON.CUSTOM_FORM_MARKETING}/${id}`)
      ?.pipe(
        tap((data: any) => this.setInCache(cacheKey, data)),
        catchError(this.handleError),
      );
  }
  getStandingById(
    id: any,
    feesId?: any,
    poolId?: any,
    teamId?: number | null,
  ): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API}${API_TOURNAMENT.STANDING}/${id}?feesId=${feesId ?? ''}&poolId=${poolId ?? ''}${teamId ? `&teamId=${teamId}` : ''}`,
      )
      ?.pipe(catchError(this.handleError));
  }
  getTournamentDivisionAndAgegroup(tournamentId: number): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API}${API_TOURNAMENT.DIVISION_AGE_GROUP}/${tournamentId}?skip=0&limit=500`,
      )
      ?.pipe(catchError(this.handleError));
  }
  getTournamentPools(tournamentId: number, feesId: any): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API}${API_TOURNAMENT.POOLS}/${tournamentId}?feesId=${feesId ?? ''}`,
      )
      ?.pipe(catchError(this.handleError));
  }
  getTournamentRangeDays(tournamentId: number): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API}tournament/marketing/date/range/${tournamentId}`,
      )
      ?.pipe(catchError(this.handleError));
  }
  getTournamentRangeDaysSummary(tournamentId: number): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API}tournament/marketing/date/range/summary/${tournamentId}`,
      )
      ?.pipe(catchError(this.handleError));
  }
  /** Public schedule download: server builds the file (full schedule + by-court grids) and
   *  returns its url. format: 'xlsx' | 'pdf'. */
  getScheduleExportUrl(
    tournamentId: number,
    format: 'xlsx' | 'pdf',
    matchType?: string,
  ): Observable<any> {
    let params = new HttpParams().set('format', format);
    if (matchType) {
      params = params.set('matchType', matchType);
    }
    return this.http.get<any>(
      `${environment.API}${API_TOURNAMENT.SCHEDULE_EXPORT}/${tournamentId}`,
      { params },
    );
  }

  getScheduledGames(
    tournamentId: number,
    feesIds?: any,
    poolId?: any,
    matchType?: string,
    bracketId?: any,
    locationId?: number | string,
    selectedDay?: number | string,
    teamId?: number | string,
    status?: string,
  ): Observable<any> {
    let params = new HttpParams();

    if (selectedDay) {
      params = params.set('dayIds', selectedDay.toString());
    }
    if (locationId) {
      params = params.set('locationId', locationId.toString());
    }
    if (feesIds) {
      params = params.set('feesIds', feesIds?.toString());
    }
    if (matchType) {
      params = params.set('matchType', matchType?.toString());
    }
    if (bracketId) {
      params = params.set('bracketId', bracketId?.toString());
    }
    if (poolId) {
      params = params.set('poolId', poolId?.toString());
    }
    if (teamId) {
      params = params.set('teamId', teamId?.toString());
    }
    if (status) {
      params = params.set('status', status.toString());
    }

    return this.http
      .get<any>(
        `${environment.API}${API_TOURNAMENT.SCHEDULE_MATCHES}/${tournamentId}`,
        { params },
      )
      .pipe(catchError(this.handleError));
  }

  // getTournamentBracketById(tournamentId: number | string, bracketId: number | string): Observable<any>{
  //   return this.http.get(`${environment.API}brackets-poc/marketing/${tournamentId}?bracketId=${bracketId}`).pipe(catchError(this.handleError));
  // }

  getTournamentBracketById(
    tournamentId: number | string,
    bracketId?: number | string,
    feesId?: any,
  ): Observable<any> {
    const params = new URLSearchParams();
    if (bracketId) params.append('bracketId', String(bracketId));
    if (feesId) params.append('feesId', String(feesId));
    return this.http
      .get(
        `${environment.API}${API_TOURNAMENT.BRACKET_LIST}/${tournamentId}?${params.toString()}`,
      )
      .pipe(catchError(this.handleError));
  }

  postTournamentBracket(payload: any): Observable<any> {
    payload['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http
      .post<any>(
        `${environment.API}${API_TOURNAMENT.ACH_PAYMENT_INTENT}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  uploadRosterDocuments(payload: any): Observable<any> {
    return this.http.post<any>(
      `${environment.API}${API_TOURNAMENT.UPLOAD_ROSTER_DOCUMENTS}`,
      payload,
    );
  }

  getAllTournamentTeamsForTournamentTeam(payload: any): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API}${API_TOURNAMENT.REGISTRATION_TEAMS_PLAYER}/${payload?.tournamentId}?skip=${payload?.skip}&limit=${payload?.limit}${payload?.poolPlayId ? `&poolPlayId=${payload?.poolPlayId}` : ''}${payload?.registrationType ? `&registrationType=${payload?.registrationType}` : ''}${payload?.divisionIds ? `&divisionIds=${payload?.divisionIds}` : ''}${payload?.ageGroupIds ? `&ageGroupIds=${payload?.ageGroupIds}` : ''}`,
      )
      ?.pipe(catchError(this.handleError));
  }

  getAllTournamentTeamsForTournamentPlayer(payload: any): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API}${API_TOURNAMENT.CUSTOM_TEAM_LIST}/${payload?.tournamentId}?skip=${payload?.skip}&limit=${payload?.limit}${payload?.poolPlayId ? `&poolPlayId=${payload?.poolPlayId}` : ''}${payload?.registrationType ? `&registrationType=${payload?.registrationType}` : ''}${payload?.divisionIds ? `&divisionIds=${payload?.divisionIds}` : ''}${payload?.ageGroupIds ? `&ageGroupIds=${payload?.ageGroupIds}` : ''}`,
      )
      ?.pipe(catchError(this.handleError));
  }

  // NSID badges are secondary info: a failed lookup hides them instead of alerting.
  getNsidTeamStatuses(teamHash: string): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API}${API_NSID_VERIFICATION.PUBLIC_TEAM_STATUS}/${teamHash}/status`,
      )
      .pipe(catchError(() => of({ status: false })));
  }

  getTeamCoaches(teamId: any, registrationId: string): Observable<any> {
    const params = new HttpParams().set('registrationId', registrationId);
    return this.http
      .get<any>(`${environment.API}${API_TOURNAMENT.TEAM_COACHES}/${teamId}`, {
        params,
      })
      .pipe(catchError(this.handleError));
  }

  addTeamCoach(payload: any, registrationId: string): Observable<any> {
    return this.http
      .post<any>(`${environment.API}${API_TOURNAMENT.ADD_TEAM_COACH}`, {
        ...payload,
        registrationId,
      })
      .pipe(catchError(this.handleError));
  }

  updateTeamCoach(payload: any, registrationId: string): Observable<any> {
    return this.http
      .patch<any>(`${environment.API}${API_TOURNAMENT.UPDATE_TEAM_COACH}`, {
        ...payload,
        registrationId,
      })
      .pipe(catchError(this.handleError));
  }

  updateTeamName(payload: any): Observable<any> {
    return this.http
      .patch<any>(
        `${environment.API}${API_TOURNAMENT.UPDATE_REGISTER_TEAM_NAME}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  removeTeamCoach(coachId: any, registrationId: string): Observable<any> {
    const params = new HttpParams().set('registrationId', registrationId);
    return this.http
      .delete<any>(
        `${environment.API}${API_TOURNAMENT.REMOVE_TEAM_COACH}/${coachId}`,
        { params },
      )
      .pipe(catchError(this.handleError));
  }

  tournamentUpdateRegisterTeamWaiver(
    registrationId: any,
    payload: any,
  ): Observable<any> {
    return this.http
      .patch<any>(
        `${environment.API}${API_TOURNAMENT.UPDATE_REGISTER_TEAM_WAIVER}/${registrationId}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  getTournamentRosterInformation(
    teamId: any,
    registrationId: string,
  ): Observable<any> {
    const params = new HttpParams().set('registrationId', registrationId);
    return this.http
      .get<any>(
        `${environment.API}${API_TOURNAMENT.ROSTER_INFORMATION}/${teamId}`,
        { params },
      )
      .pipe(catchError(this.handleError));
  }

  downloadRosterTemplate(
    tournamentId: any,
  ): Observable<{ data: { downloadUrl: string; copyUrl: string | null } }> {
    return this.http
      .get<any>(
        `${environment.API}${API_TOURNAMENT.DOWNLOAD_ROSTER_TEMPLATE}${tournamentId ? `?tournamentId=${tournamentId}` : ''}`,
      )
      .pipe(catchError(this.handleError));
  }

  exportTournamentTeamCoaches(
    teamId: any,
    allTeams: boolean,
    registrationId: string,
  ): Observable<any> {
    return this.http.get<any>(
      `${environment.API}${API_TOURNAMENT.EXPORT_TEAM_COACHES}?teamId=${teamId}&allTeams=${allTeams}&registrationId=${encodeURIComponent(registrationId)}`,
    );
  }

  downloadRosterDocumentsZip(
    teamId: any,
    id: any,
    firstName: any,
    lastName: any,
    registrationId: string,
  ): Observable<any> {
    return this.http.get<any>(
      `${environment.API}${API_TOURNAMENT.DOWNLOAD_SINGLE_ROSTER_DOCUMENTS_ZIP}/${teamId}?id=${id}&firstName=${firstName}&lastName=${lastName}&registrationId=${encodeURIComponent(registrationId)}`,
    );
  }

  downloadAllRosterDocumentsZip(
    teamId: any,
    registrationId: string,
  ): Observable<any> {
    const params = new HttpParams().set('registrationId', registrationId);
    return this.http.get<any>(
      `${environment.API}${API_TOURNAMENT.DOWNLOAD_ROSTER_DOCUMENTS_ZIP}/${teamId}`,
      { params },
    );
  }

  exportTournamentTeamInformation(
    teamId: any,
    registrationId: string,
  ): Observable<any> {
    return this.http.get<any>(
      `${environment.API}${API_TOURNAMENT.EXPORT_TEAM}?teamId=${teamId}&registrationId=${encodeURIComponent(registrationId)}`,
    );
  }

  updateTeamSocialLinks(payload: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.API}${API_TOURNAMENT.UPDATE_TEAM_SOCIAL_LINKS}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  getRegistrationTeamsForBracket(
    tournamentId: any,
    search: string = '',
  ): Observable<any> {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.http
      .get<any>(
        `${environment.API}${API_TOURNAMENT.ALL_TEAMS}/${tournamentId}${params}`,
      )
      .pipe(catchError(this.handleError));
  }

  updateBracketMatchTeam(payload: any): Observable<any> {
    return this.http
      .patch<any>(
        `${environment.API}${API_TOURNAMENT.BRACKET_MATCH_TEAM}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
}
